import React from 'react';

import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { EditorPluginAIProvider, EndExperience } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import type { InvokedFrom, InvokedFromTriggerMethod } from '../utils/analytics/analyticsFlowTypes';
import type {
	AIExperienceMachineEvent,
	Positions,
} from '../utils/xstate/get-ai-experience-service';

export type AIExperienceCommonDataContextData = {
	aiSessionId?: string;
	appearance: EditorAppearance;
	canInlineStreamChanges: () => boolean;
	configItem: EditorPluginAIConfigItem;
	editorPluginAIProvider: EditorPluginAIProvider;
	// all props under here may change based on how we wire things together
	editorView: EditorView;
	/**
	 * Called to end the experience
	 *
	 * Note: the ExperienceApplication will do additional cleanup work after calling this in
	 * its react unmount cleanup.
	 */
	endExperience: EndExperience;
	/**
	 * Engagement Platform Plugin API.
	 *
	 * You don't need to use it directly from here, use `useEngagementPlatform` hook instead.
	 */
	engagementPlatformApi?: {
		/** Starts the Engagement Platform external message with given messageId */
		startMessage: (messageId: string, variationId?: string) => Promise<boolean>;
		/** Stops the Engagement Platform external message with given messageId */
		stopMessage: (messageId: string) => Promise<boolean>;
	};
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	/** Whether the AI experience should include knowledge from the current page */
	includeKnowledgeFromCurrentPage: boolean;
	isProgrammaticScroll: React.MutableRefObject<boolean>;
	isUserScroll: React.MutableRefObject<boolean>;
	lastTriggeredFrom?: InvokedFrom;
	modalRef: React.RefObject<HTMLDivElement>;
	/**
	 * For empty selections, the start and end will be the same
	 */
	positions: Positions;
	providerFactory: ProviderFactory;
	sendToAIExperienceMachine: (event: AIExperienceMachineEvent) => void;
	setCancelRovoPrompt: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
	triggeredFor?: NodeType;

	triggerMethod?: InvokedFromTriggerMethod;
};

/**
 * We are doing null with non-null assertion operator to trick Typescript to allow null as default value.
 * We don't want to set dummy default values, that's why setting null.
 * This context is used every where using useAIExperienceCommonDataContext hook.
 * We check first if it's value is null, throws error if it's null to prevent random runtime error.
 */
export const AIExperienceCommonDataContext = React.createContext<AIExperienceCommonDataContextData>(
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	null!,
);

export const useAIExperienceCommonDataContext = () => {
	const contextData = React.useContext(AIExperienceCommonDataContext);
	if (!contextData) {
		throw new Error('Attempted to access AIExperienceCommonDataContext before its value is set');
	}
	return contextData;
};
